/**
 * Writing Quality section — AI-Like Writing and Editorial Quality scores the
 * Data team writes onto `tipranks_data.posts.qualityEvaluation` (TR-26736).
 */

export type IssueSeverity = "low" | "medium" | "high";

/** One metric (AI-Like Writing or Editorial Quality) as stored. */
export interface QualityScore {
  total: number;
  max: number;
  /** The five scoring dimensions, keyed by camelCase name; they sum to `total`. */
  dimensions: Record<string, number>;
}

export interface QualityIssue {
  id: string;
  excerpt: string;
  description: string;
  dimension: string;
  severity: IssueSeverity;
}

export interface QualityRecommendation {
  issueId: string;
  text: string;
}

/** `posts.qualityEvaluation`, schemaVersion 1. */
export interface QualityEvaluation {
  meta: { evaluatedAt: string; model: string; schemaVersion: number };
  verdict: { recommendedAction: string; summary: string };
  scores: { aiWriting: QualityScore; editorialQuality: QualityScore };
  issues: QualityIssue[];
  recommendations: QualityRecommendation[];
}

/** A list row: one evaluated article, scores only. */
export interface WritingQualityArticle {
  id: string;
  title: string;
  /** Absolute tipranks.com URL of the published article. */
  url: string;
  publishedAt: string;
  authorSlug: string;
  authorName: string;
  aiWriting: number;
  editorialQuality: number;
  recommendedAction: string;
}

export interface WritingQualityArticlesResponse {
  articles: WritingQualityArticle[];
  /** True when the range held more articles than the response cap. */
  truncated: boolean;
}

export interface WritingQualityArticleDetail extends WritingQualityArticle {
  evaluation: QualityEvaluation;
}

export interface WritingQualityArticleDetailResponse {
  article: WritingQualityArticleDetail;
}

export interface WritingQualitySummary {
  articles: number;
  writers: number;
  avgAiWriting: number | null;
  avgEditorialQuality: number | null;
}

export interface WriterQualityRow {
  authorSlug: string;
  authorName: string;
  articles: number;
  avgAiWriting: number;
  avgEditorialQuality: number;
}

function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

export function summarizeWritingQuality(
  articles: WritingQualityArticle[]
): WritingQualitySummary {
  return {
    articles: articles.length,
    writers: new Set(articles.map((a) => a.authorSlug)).size,
    avgAiWriting: average(articles.map((a) => a.aiWriting)),
    avgEditorialQuality: average(articles.map((a) => a.editorialQuality)),
  };
}

/** Per-writer averages, most articles first. */
export function groupByWriter(articles: WritingQualityArticle[]): WriterQualityRow[] {
  const byAuthor = new Map<string, WritingQualityArticle[]>();
  for (const a of articles) {
    const list = byAuthor.get(a.authorSlug);
    if (list) list.push(a);
    else byAuthor.set(a.authorSlug, [a]);
  }
  return [...byAuthor.values()]
    .map((list) => ({
      authorSlug: list[0].authorSlug,
      authorName: list[0].authorName,
      articles: list.length,
      avgAiWriting: average(list.map((a) => a.aiWriting)) ?? 0,
      avgEditorialQuality: average(list.map((a) => a.editorialQuality)) ?? 0,
    }))
    .sort((a, b) => b.articles - a.articles || a.authorName.localeCompare(b.authorName));
}

/** `major_edit` → "Major edit", `genericLanguage` → "Generic language". */
export function humanizeKey(key: string): string {
  const words = key
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
